<template>
  <div class="certification">
    <div class="certification__wrapper">
      <div
        class="certification__body"
        :class="[
          !haveVouchers && 'certification__body--no-vouchers',
          (attemptInReview || attemptPassed) &&
            'certification__body--has-upper-badge',
        ]"
      >
        <CertStatus
          class="certification__status-badge"
          v-if="attemptInReview || attemptPassed"
          :status="statusBadgeText"
          :showStatus="true"
        />
        <div class="certification__badge">
          <img
            class="certification__badge-logo"
            :src="certification.logo_image_url"
          />
        </div>
        <div class="certification__information">
          <div class="certification__information__group">
            <div class="certification__information__group-left">
              <div class="certification__information__version-wrapper">
                <div
                  v-if="hasLab"
                  class="certification__information__haslab-badge"
                >
                  <LabIcon
                    class="certification__information__haslab-badge-icon"
                  />
                  Hands-on Lab!
                </div>
                <CertificationsListItemVersionBadge
                  class="certification__information__version"
                  v-bind="{
                    versions: certification.versions || emptyVersions,
                    latestVersion: certification.latest_version || '',
                    status: '',
                  }"
                />
              </div>
              <h3 class="certification__information-name">
                {{ certification.name }}
              </h3>
              <div
                class="certification__tips-wrapper"
                v-if="!isMobileOrDesktop"
              >
                <strong>Before beginning the exam, we recommend:</strong>
                <ol class="certification__tips">
                  <li class="certification__tip">
                    Watch the ICCA lab introduction video, so you are oriented
                    to our new lab layout.
                  </li>
                  <li class="certification__tip">
                    Ensure that you are in a comfortable, quiet location with a
                    stable internet connection, where you will not be
                    interrupted for the duration of exam.
                  </li>
                  <li class="certification__tip">
                    Review this
                    <a href="//info.ine.com/icca-certification/" target="_blank"
                      >certification's FAQs</a
                    >
                    before beginning the exam for all the latest updates and
                    information.
                  </li>
                </ol>
              </div>
            </div>
            <div class="certification__information__group-right">
              <div v-if="canShowLabStatus" class="certification__lab-status">
                LAB RUNNING
                {{
                  $duration.hhmmss(this.certificationLab.session.running_time)
                }}
                <span class="certification__lab-status__ball"></span>
              </div>
              <CertificationDetails
                :difficulty="certification.difficulty || ''"
                :timeLimit="
                  certification.time_cap_in_secs
                    ? $duration.hhmmss(certification.time_cap_in_secs)
                    : 0
                "
                :questions="certification.amount_questions || 0"
                :objectives="labObjectives"
              />
              <div class="certification__tips-wrapper" v-if="isMobileOrDesktop">
                <strong>Before beginning the exam, we recommend:</strong>
                <ol class="certification__tips">
                  <li class="certification__tip">
                    Watch the ICCA lab introduction video, so you are oriented
                    to our new lab layout.
                  </li>
                  <li class="certification__tip">
                    Ensure that you are in a comfortable, quiet location with a
                    stable internet connection, where you will not be
                    interrupted for the duration of exam.
                  </li>
                  <li class="certification__tip">
                    Review this
                    <a href="//info.ine.com/icca-certification/" target="_blank"
                      >certification's FAQs</a
                    >
                    before beginning the exam for all the latest updates and
                    information.
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div class="certification__cta-wrapper">
          <div class="certification__messages">
            <div class="certification__messages-purchase" v-if="canNotTakeExam">
              <div class="certification__messages-icon">
                <AlertCircleIcon />
                <div>
                  You have no available vouchers.
                  <a
                    v-if="isBeta"
                    href="//info.ine.com/icca-certification/"
                    target="_blank"
                    rel="noopener nofollow"
                  >
                    This is a closed beta
                  </a>
                </div>
              </div>
            </div>
            <div
              class="certification__messages-progress"
              v-if="attemptInProgress"
            >
              <AlertCircleIcon />
              This exam is in progress. You have {{ remainingHours }} remaining.
            </div>
            <div class="certification__messages-progress" v-if="canDoARetake">
              <span class="certification__messages-review--red"></span>
              Failed: You have
              {{ lastActiveVoucher ? lastActiveVoucher.remaining_attempts : 0 }}
              {{
                lastActiveVoucher.remaining_attempts === 1
                  ? 'retake'
                  : 'retakes'
              }}
              available until
              {{ retakeExpirationShortDate }}
            </div>
            <div class="certification__messages-review" v-if="attemptInReview">
              <span
                class="certification__messages-review-status--review"
              ></span>
              Pending Review: We're reviewing your exam. You'll get a
              notification when the results are in.
            </div>
          </div>
          <div class="certification__messages-passed" v-if="attemptPassed">
            <router-link
              :to="{
                name: 'user-dashboard',
                params: { mode: 'certifications' },
              }"
              class="btn btn--primary certification__download-link"
              >See your certification</router-link
            >
          </div>
          <div class="certification__cta-wrapper__states">
            <a
              v-if="
                (noVouchers && !certInBeta) ||
                  (!certification.user_status && !certInBeta)
              "
              :href="
                `//${checkoutUrl}?products=ine-ind-cert-icca-cloud-associate`
              "
              target="_blank"
              rel="noopener nofollow"
              class="btn btn--primary certification__cta"
            >
              Shop vouchers
            </a>
            <a
              v-if="
                (noVouchers && certInBeta) ||
                  (!certification.user_status && certInBeta)
              "
              :href="'//info.ine.com/icca-certification/'"
              target="_blank"
              rel="noopener nofollow"
              class="btn btn--primary certification__cta"
            >
              Learn more
            </a>
            <button
              v-if="!canNotTakeExam && !noCTAButton"
              @click="setCertificationAndGo()"
              class="btn btn--primary certification__cta"
            >
              {{ ctaButtonText }}
            </button>
          </div>
        </div>
      </div>
      <div class="certification__vouchers" v-if="haveVouchers">
        <CertificateVoucher
          v-for="voucher in activeVouchers"
          :key="voucher.id"
          :voucher="voucher"
          :active="true"
          class="certification__vouchers__active"
        />
        <CertificateVoucher
          v-for="voucher in inactiveVouchers"
          :key="voucher.id"
          :voucher="voucher"
          :active="false"
          class="certification__vouchers__inactive"
        />
      </div>
    </div>
  </div>
</template>
<script>
import {
  differenceInSeconds,
  differenceInHours,
  intervalToDuration,
} from 'date-fns'
import { format } from 'date-fns-tz'
import { AlertCircleIcon } from 'vue-feather-icons'
import CONSTANTS from '@/constants'
import { hasPassedAttempt } from '@/helpers'
import CertificateVoucher from '@/components/certifications/CertificateVoucher.vue'
import CertificationsListItemVersionBadge from '@/components/user-dashboard/certifications/CertificationsListItemVersionBadge.vue'
import CertificationDetails from '@/components/certifications/CertificationDetails.vue'
import CertStatus from '@/components/properties/CertStatus.vue'
import LabIcon from '@/assets/icons/course/lab/lab_icon.svg'

export default {
  name: 'Certification',
  components: {
    LabIcon: { ...LabIcon, name: 'LabIcon' },
    CertificateVoucher,
    CertificationsListItemVersionBadge,
    CertificationDetails,
    CertStatus,
    AlertCircleIcon,
  },
  props: {
    certification: {
      type: Object,
      required: true,
    },
  },
  data: () => {
    return {
      emptyVersions: [],
      certificationLab: null,
      isMobileOrDesktop: false,
    }
  },
  computed: {
    learningAreasSlugs() {
      const LAs = {
        ...CONSTANTS.LEARNING_AREAS.PRIMARY,
        ...CONSTANTS.LEARNING_AREAS.SECONDARY,
      }
      return Object.values(LAs).reduce((acc, la) => {
        acc[la.id] = la.slug
        return acc
      }, {})
    },
    canNotTakeExam() {
      // when we have to buy:
      // no cert
      // no user
      // no voucher
      // no attempt and no progress, review or passed
      // no matters about attempts, but no active and no progress, review or passed
      return (
        !this.certification ||
        !this.certification.user_status ||
        !this.lastVoucher ||
        (!this.lastVoucher.remaining_attempts &&
          !this.attemptInProgress &&
          !this.attemptInReview &&
          !this.attemptPassed) ||
        (!this.lastActiveVoucher &&
          !this.attemptInProgress &&
          !this.attemptInReview &&
          !this.attemptPassed)
      )
    },
    noCTAButton() {
      return this.attemptInReview || this.attemptPassed
    },
    noVouchers() {
      return (
        !this.noCTAButton &&
        !this.activeVouchers?.length &&
        this.certification.user_status
      )
    },
    certInBeta() {
      return this.isBeta || this.certification.versions?.[0].is_beta
    },
    attemptInReview() {
      if (
        this.lastAttempt &&
        this.lastAttempt.status ===
          CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.REVIEW
      ) {
        return this.lastAttempt
      }
      return null
    },
    attemptPassed() {
      return hasPassedAttempt(this.lastAttempt)
    },
    attemptFailed() {
      if (
        this.lastAttempt &&
        (this.lastAttempt.status ===
          CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.FINISHED ||
          this.lastAttempt.status ===
            CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.TIMEDOUT) &&
        !this.attemptPassed
      ) {
        return this.lastAttempt
      }
      return null
    },
    canDoARetake() {
      return (
        this.lastActiveVoucher &&
        this.lastActiveVoucher.remaining_attempts &&
        this.lastActiveVoucher.next_attempt_expiration_date &&
        this.attemptFailed
      )
    },
    attemptInProgress() {
      if (
        this.lastAttempt &&
        this.lastAttempt.status ===
          CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.IN_PROGRESS &&
        differenceInSeconds(
          new Date(this.lastAttempt.expiration_date).getTime(),
          new Date().getTime()
        ) >= 0
      ) {
        return this.lastAttempt
      }
      return null
    },
    lastAttempt() {
      let allAttempts = []
      if (this.activeVouchers?.length) {
        this.activeVouchers.map(voucher => {
          allAttempts = [...allAttempts, ...voucher.active_attempts]
          allAttempts = [...allAttempts, ...voucher.inactive_attempts]
        })
      }
      if (this.inactiveVouchers?.length) {
        this.inactiveVouchers.map(voucher => {
          allAttempts = [...allAttempts, ...voucher.active_attempts]
          allAttempts = [...allAttempts, ...voucher.inactive_attempts]
        })
      }
      return allAttempts.length ? allAttempts[0] : null
    },
    lastVoucher() {
      if (this.activeVouchers?.length) {
        return this.activeVouchers[0]
      }
      if (this.inactiveVouchers?.length) {
        return this.inactiveVouchers[0]
      }
      return null
    },
    lastActiveVoucher() {
      if (this.activeVouchers?.length) {
        return this.activeVouchers[0]
      }
      return null
    },
    usedHours() {
      const duration = differenceInHours(
        new Date().getTime(),
        new Date(this.attemptInProgress.created).getTime()
      )
      return duration ? `${duration} hours` : 'some minutes'
    },
    totalHours() {
      const duration = differenceInHours(
        new Date(this.attemptInProgress.expiration_date).getTime(),
        new Date(this.attemptInProgress.created).getTime()
      )
      return duration ? `${duration} hours` : 'Minutes'
    },
    attemptExpirationDate() {
      return format(
        new Date(this.attemptInProgress.expiration_date),
        'LLL d, yyyy  hh:mmaaa'
      )
    },
    retakeExpirationShortDate() {
      return format(
        new Date(this.lastVoucher.next_attempt_expiration_date),
        'LLL d, yyyy'
      )
    },
    retakeExpirationDate() {
      return format(
        new Date(this.lastVoucher.next_attempt_expiration_date),
        'LLL d, yyyy  hh:mmaaa'
      )
    },
    remainingHours() {
      const duration = intervalToDuration({
        start: new Date(),
        end: new Date(this.attemptInProgress.expiration_date),
      })

      return `${duration.hours}h${duration.minutes}m`
    },
    ctaButtonText() {
      let text = 'Start exam'
      if (this.attemptInProgress) {
        text = 'Resume exam'
      } else if (this.canDoARetake) {
        text = 'Retake exam'
      }
      return text
    },
    haveVouchers() {
      return (
        this.certification.user_status?.active_vouchers?.length ||
        this.certification.user_status?.inactive_vouchers?.length
      )
    },
    activeVouchers() {
      if (!this.certification.user_status) return null
      const {
        certification: {
          user_status: { active_vouchers },
        },
      } = this
      return active_vouchers
    },
    inactiveVouchers() {
      if (!this.certification.user_status) return null
      const {
        certification: {
          user_status: { inactive_vouchers },
        },
      } = this
      return inactive_vouchers
    },
    isBeta() {
      return (
        this.certification?.versions?.[0]?.is_beta ||
        this.lastVoucher?.certification_version?.is_beta
      )
    },
    hasLab() {
      return this.certification.versions?.[0]?.practice_lab
    },
    labObjectives() {
      return this.certificationLab
        ? this.certificationLab.lab_objectives?.length
        : 0
    },
    statusBadgeText() {
      if (this.attemptInReview) return 'Pending Review'
      if (this.attemptPassed) return 'awarded'
      return ''
    },
    canShowLabStatus() {
      return (
        this.hasLab &&
        this.certificationLab?.session?.status ===
          CONSTANTS.LABS.SESSION_STATUS.STARTED
      )
    },
    checkoutUrl() {
      return process.env.VUE_APP_CHECKOUT_URL
    },
  },
  methods: {
    getBackgroundBadge(la) {
      return require('@/assets/images/certification/background-cert-cloud.png')
    },
    setCertificationAndGo() {
      this.$store.dispatch(
        'certifications/updateCurrentCertification',
        this.certification
      )
      this.$router.push({
        name: 'certification-exam',
        params: {
          voucherId: this.activeVouchers[0].id,
          attemptId: this.attemptInProgress ? this.attemptInProgress.id : null,
        },
      })
    },
  },
  async mounted() {
    this.isMobileOrDesktop = window.innerWidth < 1024
    this.$nextTick(() => {
      window.addEventListener('resize', () => {
        this.windowWidth = window.innerWidth
      })
    })
    if (this.hasLab) {
      const response = await this.$store.dispatch(
        'labs/getLabByIdWithoutContext',
        this.hasLab
      )
      this.certificationLab = response
    }
  },
  destroyed() {
    window.removeEventListener('resize', () => null)
  },
}
</script>
<style lang="scss" scoped>
.certification {
  color: var(--white);
  margin-bottom: 5em;

  &__body {
    display: flex;
    min-height: 333px;
    background: var(--glass-mid-gray);
    border-radius: 10px;
    height: 100%;
    flex-wrap: wrap;
    padding: 25px 20px;
    align-items: center;
    position: relative;
    @include mobile {
      align-items: flex-start;
    }

    &--no-vouchers {
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
    }
    &--has-upper-badge {
      padding-top: 40px;
    }
  }
  &__badge {
    position: relative;
    min-width: 230px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 30px;
    margin-top: 40px;
    flex: 1 1 100%;
    @include tablet {
      flex: 0;
      justify-content: center;
      margin-left: 0;
      margin-right: 20px;
    }

    @include desktop {
      flex: 0 1 30%;
    }

    &-logo {
      max-height: 300px;

      @include desktop {
        max-height: 450px;
      }
    }
  }
  &__status-badge {
    position: absolute;
    top: 0;
    left: 0;
  }
  &__information {
    flex: 1;
    padding: 30px 0 0 0;

    @include desktop {
      padding: 40px 30px 30px 60px;
    }
    &__group {
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      @include tablet {
        flex-direction: row;
      }
      @include desktop {
        justify-content: space-around;
      }
      &-right {
        width: 100%;
        @include tablet {
          width: auto;
          min-width: 300px;
          padding-left: 30px;
        }

        @include desktop {
          flex-basis: 45%;
        }
      }
      &-left {
        @include desktop {
          flex-basis: 40%;
        }
      }
    }
    &__version-wrapper {
      display: flex;
      align-items: stretch;

      width: 100%;

      &--approved {
        justify-content: flex-end;
      }

      @include tablet {
        justify-content: left;
      }
    }
    &-cert {
      display: flex;
    }
    &-name {
      font-size: rem(20px);
      line-height: 26px;
      font-family: var(--font-bold);
      display: flex;
      align-items: center;
      margin: 15px 0px;

      @include tablet {
        margin: 20px 0;
      }
    }
    &-subtitle {
      color: var(--lighter-gray);
      font-size: 18px;
      margin-bottom: 10px;
      margin-right: 40px;
      font-family: var(--font-medium);
    }
    &-data {
      font-size: 16px;
      margin-bottom: 13px;
      font-family: var(--font-medium);
    }
    &__haslab-badge {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      padding: 8px 14px;
      background: #88a9ff;
      border-radius: 100px;
      color: #1e1e1e;
      margin-right: 15px;
      &-icon {
        margin-right: 10px;
        fill: #1e1e1e;
        path {
          stroke: none;
        }
      }
    }
  }
  &__tips {
    margin: 15px 0;
    padding-inline-start: 20px;
  }
  &__tip {
    position: relative;
    font-size: 12px;
    line-height: 14px;
    color: var(--light-blue-gray);
    @include tablet {
      padding: 0;
      font-size: 16px;
      line-height: 24px;
    }
    a {
      color: var(--light-blue-gray);
      text-decoration: underline;
      &:hover {
        color: var(--white);
      }
    }
  }
  &__cta-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-content: center;
    margin: 20px 0 30px 0;
    flex: 0 1 100%;

    @include mobile {
      flex-direction: row;
    }
  }

  &__cta {
    margin-top: 20px;
    width: 100%;

    @include tablet {
      margin-top: 0px;
      width: auto;
    }
  }

  &__messages {
    display: flex;
    align-items: center;
    text-align: right;
    margin-right: auto;
    &-purchase {
      color: var(--light-blue-gray);
      display: flex;
      flex-direction: column;
      svg {
        margin-right: 15px;
        color: white;
      }
      a {
        color: var(--light-blue-gray);
        &:hover {
          color: var(--white);
        }
      }

      @include tablet {
        flex-direction: row;
      }
    }
    &-icon {
      text-align: left;
      display: flex;
      align-items: center;
    }
    &-progress {
      display: flex;
      align-items: center;
      color: var(--light-blue-gray);
      svg {
        margin-right: 15px;
      }
    }
    &-review {
      color: var(--light-blue-gray);
      display: flex;
      &-status {
        &--review {
          width: 5px;
          height: 17px;
          background: #eeeeee;
          border-radius: 10px;
          margin-right: 15px;
          border-radius: 10px;
        }
      }
      &--red {
        width: 5px;
        height: 17px;
        background: linear-gradient(180deg, #e52a2a 0%, #ff5e5e 100%);
        border-radius: 10px;
        margin-right: 15px;
        border-radius: 10px;
      }
    }
    &-passed {
      margin-bottom: 30px;
    }
  }
  &__lab-status {
    padding: 10px 10px 10px 15px;
    border: 1px solid var(--tundora-gray);
    box-sizing: border-box;
    border-radius: 30px;
    font-size: rem(12px);
    line-height: 14px;
    color: var(--light-blue-gray);
    display: flex;
    align-items: center;
    width: fit-content;
    margin-bottom: 20px;
    @include mobile {
      margin-left: auto;
    }
    &__ball {
      width: 13px;
      height: 13px;
      background: #70ffd4;
      border-radius: 50%;
      margin-left: 10px;
      display: block;
    }
  }
  &__download-link {
    width: 100%;
  }
}
</style>
